import { useCallback, useState } from 'react';
import { Linking, Platform } from 'react-native';

import { apiService } from '@/services/api-service';
import { Alert } from '@/utils/alert';

/**
 * Start the Dropbox OAuth flow.
 *
 * Shared by Settings → Integrations and the Documents empty state so the two
 * entry points cannot drift: the server issues a single-use `state` per attempt,
 * so "which button did I press" must not change the request shape.
 *
 * Always a FULL-PAGE navigation rather than a popup — Dropbox refuses to render
 * inside an iframe, and a window.open() this far from the original click is
 * treated as an unrequested popup and blocked.
 */
export function useDropboxConnect(): { connect: () => Promise<void>; connecting: boolean } {
	const [connecting, setConnecting] = useState(false);

	const connect = useCallback(async () => {
		setConnecting(true);
		try {
			const res = await apiService.fetchApi('/integrations/dropbox/connect', { method: 'POST' });
			if (!res?.authorizeUrl) throw new Error('No authorize URL returned');
			if (Platform.OS === 'web') window.location.assign(res.authorizeUrl);
			else await Linking.openURL(res.authorizeUrl);
		} catch (err) {
			Alert.alert(
				'Could not start Dropbox connection',
				err instanceof Error ? err.message : 'Unknown error',
			);
		} finally {
			setConnecting(false);
		}
	}, []);

	return { connect, connecting };
}
